import { CSSProperties, FC, useEffect } from "react";
import { AppShell, Grid, Box } from "@mantine/core";
import { AppHeader } from "./Components/Layout/AppHeader";
import { AppFooter } from "./Components/Layout/AppFooter";
import { useMediaQuery } from "@mantine/hooks";
import { Outlet, ScrollRestoration, useOutlet } from "react-router-dom";
import { Homepage } from "./Components/Pages/Homepage";
import { CurrentGame } from "./Components/Widgets/CurrentGame";
import { Navigation } from "./Components/Layout/Navigation";
import { BottomNav } from "./Components/Layout/BottomNav";
import { Stream } from "./Components/Widgets/Stream";
import { OfflineIndicator } from "../../Components/OfflineIndicator.tsx";
import { SponsorAd } from "../../Components/SponsorAd.tsx";
import { getPresentationParamsForOrigin } from "../../getPresentationParamsForOrigin.ts";
import { useMetaDescription } from "../../hooks/useMetaDescription.ts";

export const App: FC = () => {
  const isDesktop = useMediaQuery("(min-width: 1200px)");
  const isMobile = useMediaQuery("(max-width: 48em)");
  const outlet = useOutlet();
  const headerHeight = "70px";
  const bottomNavHeight = "60px";

  // Edge-to-edge (viewport-fit=cover): the navbar absorbs the left safe area
  // when visible, otherwise the main content has to.
  const navbarOffset = "var(--app-shell-navbar-offset, 0rem)";
  const mainStyle: CSSProperties & Record<`--${string}`, string> = {
    "--app-inset-start": `calc(max(${navbarOffset}, env(safe-area-inset-left, 0px)) - ${navbarOffset})`,
    "--app-inset-end": "env(safe-area-inset-right, 0px)",
    paddingTop: "var(--app-shell-header-offset, 0rem)",
    paddingInlineStart: `calc(${navbarOffset} + var(--app-inset-start) + var(--app-shell-padding))`,
    paddingInlineEnd: "calc(var(--app-inset-end) + var(--app-shell-padding))",
    paddingBottom: isMobile
      ? `calc(${bottomNavHeight} + env(safe-area-inset-bottom, 0px))`
      : undefined,
  };

  const competitionName = getPresentationParamsForOrigin().competitionName;

  // Set default title and meta description for homepage (when no outlet is active)
  useEffect(() => {
    if (!outlet) {
      document.title = competitionName;
    }
  }, [outlet, competitionName]);

  useMetaDescription(
    !outlet
      ? `Live-Ergebnisse, Tabellen, Spielplan und Topscorer beim ${competitionName}.`
      : undefined,
  );

  return (
    <>
      <ScrollRestoration />
      <AppShell
        header={{
          height: `calc(${headerHeight} + env(safe-area-inset-top, 0px))`,
        }}
        navbar={{
          width: "calc(250px + env(safe-area-inset-left, 0px))",
          breakpoint: "sm",
          collapsed: { mobile: true },
        }}
        padding="md"
        styles={(theme) => ({
          main: { backgroundColor: theme.colors.gray[0] },
        })}
      >
        <AppHeader />

        <Navigation />

        <AppShell.Main mt={0} style={mainStyle}>
          <OfflineIndicator />
          {isDesktop ? (
            <Grid>
              <Grid.Col span={4}>
                {outlet != null ? <Outlet /> : <Homepage />}
              </Grid.Col>
              <Grid.Col span={8}>
                <Box
                  w="100%"
                  style={{
                    position: "sticky",
                    top: "calc(var(--app-shell-header-offset, 0rem) + var(--app-shell-padding))",
                  }}
                >
                  <Stream />
                  <SponsorAd />
                </Box>
              </Grid.Col>
            </Grid>
          ) : (
            <>
              {/* CurrentGame nur auf Unterseiten zeigen, Homepage hat MatchCenterHero */}
              {outlet != null && <CurrentGame />}
              {outlet != null ? <Outlet /> : <Homepage />}
            </>
          )}

          <AppFooter />
        </AppShell.Main>
      </AppShell>

      <BottomNav />
    </>
  );
};
